import { getUserInfos, updateAuthenticatedUser, getAuthMe } from './../clients/Crm/Api/backend_helper';
import { setAuthorization } from "clients/Crm/Api/api_helper";
import { makeObservable, observable, action, computed } from "mobx";

import { LoginResponse } from "clients/AuthClient";
import { getProfileImage } from "clients/Crm/UsersClient";
import { expandRole, normalizeRole, type Role } from "v2/auth/roles";

type User = {
  ClientId: string;
  UserId: string;
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string[];
  profilePictureUri
};

export class UserStore {
  hasCheckedAuth: boolean = false;
  name: string = "";
  username: string = "";
  email: string = "";
  isAuthorized: boolean = false;
  permissions: string[] = [];
  role: Role = "agent_hybrid";
  entitlements: Record<string, boolean> = {};
  // True once the agency has ANY product configured — gates strictly; false = fail-open.
  entitlementsConfigured: boolean = false;

  // Cross-tenant super-admin (RealSoftware staff). When true the client-switcher
  // is available; `impersonating` is true once switched into a customer agency.
  isSuperAdmin: boolean = false;
  impersonating: boolean = false;
  impersonatingAgency: string = "";

  rootStore: any;

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.isAuthorized = false;
    this.hasCheckedAuth = false;
    makeObservable(this, {
      hasCheckedAuth: observable,
      name: observable,
      username: observable,
      email: observable,
      isAuthorized: observable,
      permissions: observable,
      role: observable,
      entitlements: observable,
      entitlementsConfigured: observable,
      isSuperAdmin: observable,
      impersonating: observable,
      impersonatingAgency: observable,
      login: action,
      checkLogin: action,
      refreshFromMe: action,
      validateSession: action,
      logout: action,
      impersonate: action,
      exitImpersonation: action,
      isLoggedIn: computed,
      getUserInfo: action,
      getUserImage: action,
    });
  }

  getUser = (decodedToken: any): User => {
    const user = {
      UserId: decodedToken['UserId'],
      id: decodedToken['UserId'],
      email: decodedToken['email'],
      firstName: decodedToken['firstName'],
      lastName: decodedToken['lastName'],
      name: decodedToken['email'],
      // id: decodedToken[ClaimType.NameIdentifier],
      // name: decodedToken[ClaimType.Name],
      // firstName: decodedToken[ClaimType.GivenName],
      // lastName: decodedToken[ClaimType.Surname],
      // email: decodedToken[ClaimType.Email],
      // profilePictureUri: decodedToken[ClaimType.ProfilePictureUri],
    };

    return user as any as User;
  };

  login = async (res: LoginResponse) => {
    // The signin response carries identity + role directly, so we no longer
    // decode the JWT client-side (the old hardcoded secret was meaningless).
    const user = this.getUser(res);
    const role = normalizeRole((res as any).role);

    this.name = user.name;
    this.isAuthorized = true;
    this.role = role;
    this.permissions = expandRole(role);

    const superAdmin = (res as any).isSuperAdmin === true;
    this.isSuperAdmin = superAdmin;
    // A fresh sign-in is never an impersonated session.
    this.impersonating = false;
    this.impersonatingAgency = "";

    // Make every subsequent request (v1 + v2) carry the new token.
    setAuthorization(res.accessToken);

    window.localStorage.setItem("authUser", JSON.stringify(res));

    var userInfo = await getUserInfos({ email: user.email });

    updateAuthenticatedUser({ ...res, ...userInfo });

    window.localStorage.setItem("__accessToken", res.accessToken);
    window.localStorage.setItem("__refreshToken", res.refreshToken);
    window.localStorage.setItem("__name", user.name);
    window.localStorage.setItem("__username", user.email);
    window.localStorage.setItem("__profilePictureUri", user.profilePictureUri);
    window.localStorage.setItem("role", role);
    if ((res as any).AgentId) {
      window.localStorage.setItem("agentId", (res as any).AgentId);
    } else {
      // No linked agent on THIS account — clear any previous user's agentId or
      // the new session inherits their per-agent scope (shared office machines).
      window.localStorage.removeItem("agentId");
    }

    // Super-admin: stash the bare admin session so we can return to the picker
    // ("exit impersonation") after switching into an agency. Clear any stale
    // impersonation flags from a previous session.
    window.localStorage.removeItem("__impersonating");
    window.localStorage.removeItem("__impersonatingAgency");
    if (superAdmin) {
      window.localStorage.setItem("__superAdmin", "true");
      window.localStorage.setItem("__superAdminToken", res.accessToken);
      window.localStorage.setItem("__superAdminAuthUser", JSON.stringify(res));
    } else {
      window.localStorage.removeItem("__superAdmin");
      window.localStorage.removeItem("__superAdminToken");
      window.localStorage.removeItem("__superAdminAuthUser");
    }

    // Refresh role + product entitlements for the NEW agency from /me (KAN-33).
    // checkLogin() only runs once on App mount, so without this a fresh login
    // (or token swap) would keep the prior session's entitlements until a hard
    // refresh. Best-effort — a transient /me failure must not block sign-in.
    try {
      await this.refreshFromMe(superAdmin);
    } catch {
      /* non-fatal; validateSession()/next mount will reconcile */
    }
  };

  /**
   * Switch the live session into a customer agency. `res` is the response from
   * /api/admin/impersonate (a token scoped to that agency, role owner, still
   * carrying the super-admin claim). Mirrors login()'s persistence so the rest
   * of the app — which reads ClientId/role from localStorage — just works.
   */
  impersonate = async (res: any, agencyName: string) => {
    setAuthorization(res.accessToken);

    const role = normalizeRole(res.role);
    this.role = role;
    this.permissions = expandRole(role);
    this.isAuthorized = true;
    this.isSuperAdmin = true;
    this.impersonating = true;
    this.impersonatingAgency = agencyName || "";

    // Re-hydrate the agency owner's extended profile (portal AgentIds etc.) so
    // v1 features that read those off authUser keep working while switched in.
    let userInfo: any = {};
    if (res.ownerEmail) {
      try {
        userInfo = await getUserInfos({ email: res.ownerEmail });
      } catch {
        userInfo = {};
      }
    }
    // Force OFFICE-WIDE: getUserInfos() returns the owner's profile (incl. their
    // listing-agent AgentId), but support needs the WHOLE agency, not the owner's
    // personal slice. Null out AgentId so getSession().agentId resolves empty and
    // every page (dashboard/listings/tasks/contacts) shows office-wide, matching
    // role=owner. Other portal IDs (DomainAgencyId, REA AgentId, …) are kept.
    const authUser = { ...res, ...userInfo, AgentId: null };
    updateAuthenticatedUser(authUser);

    window.localStorage.setItem("__accessToken", res.accessToken);
    window.localStorage.setItem("role", role);
    window.localStorage.removeItem("agentId");

    const displayName =
      `${authUser.FirstName ?? authUser.firstName ?? ""} ${authUser.LastName ?? authUser.lastName ?? ""}`.trim() ||
      agencyName ||
      authUser.email ||
      "";
    this.name = displayName;
    window.localStorage.setItem("__name", displayName);
    window.localStorage.setItem("__username", authUser.email || res.email || "");
    window.localStorage.setItem("__impersonating", "true");
    window.localStorage.setItem("__impersonatingAgency", agencyName || "");

    // Re-derive product entitlements for the IMPERSONATED agency from /me
    // (KAN-33). Switch-client navigates without a reload, so without this the
    // super-admin keeps the prior agency's Trust/PM nav until a hard refresh.
    // keep=true: the impersonation token still carries the super-admin claim.
    try {
      await this.refreshFromMe(true);
    } catch {
      /* non-fatal; entitlements reconcile on next /me */
    }
    // refreshFromMe() may set role from the token; force office-wide owner role.
    this.role = role;
    this.permissions = expandRole(role);
    this.impersonating = true;
    window.localStorage.setItem("role", role);
  };

  /** Drop the impersonated agency and restore the bare super-admin session. */
  exitImpersonation = () => {
    const baseToken = window.localStorage.getItem("__superAdminToken");
    const baseAuthRaw = window.localStorage.getItem("__superAdminAuthUser");
    if (!baseToken || !baseAuthRaw) return;
    const baseAuth = JSON.parse(baseAuthRaw);

    setAuthorization(baseToken);
    const role = normalizeRole(baseAuth.role);
    this.role = role;
    this.permissions = expandRole(role);
    this.isSuperAdmin = true;
    this.impersonating = false;
    this.impersonatingAgency = "";

    updateAuthenticatedUser(baseAuth);
    window.localStorage.setItem("__accessToken", baseToken);
    window.localStorage.setItem("role", role);
    window.localStorage.removeItem("agentId");
    window.localStorage.setItem("__name", baseAuth.email || "Super Admin");
    window.localStorage.setItem("__username", baseAuth.email || "");
    window.localStorage.removeItem("__impersonating");
    window.localStorage.removeItem("__impersonatingAgency");
  };

  checkLogin = () => {
    if (this.hasCheckedAuth) return;
    this.hasCheckedAuth = true;

    const name = window.localStorage.getItem("__name");
    const token = window.localStorage.getItem("__accessToken");
    if (name && token) {
      // Rehydrate synchronously so gating works on the first paint...
      this.name = name;
      this.isAuthorized = true;
      this.role = normalizeRole(window.localStorage.getItem("role"));
      this.permissions = expandRole(this.role);
      try {
        const e = window.localStorage.getItem("entitlements");
        this.entitlements = e ? JSON.parse(e) : {};
      } catch {
        this.entitlements = {};
      }
      this.entitlementsConfigured = window.localStorage.getItem("entitlementsConfigured") === "true";
      this.isSuperAdmin = window.localStorage.getItem("__superAdmin") === "true";
      this.impersonating = window.localStorage.getItem("__impersonating") === "true";
      this.impersonatingAgency = window.localStorage.getItem("__impersonatingAgency") || "";
      setAuthorization(token);
      // ...then validate the token against the server in the background and
      // refresh the role + product entitlements (covers legacy tokens).
      this.validateSession();
    }
  };

  /**
   * Fetch /api/auth/me for the CURRENT token and apply role + product
   * entitlements + super-admin/impersonation context to the store. Used by
   * login(), impersonate() and validateSession() so a token swap always
   * re-derives entitlements for the NEW agency (KAN-33), not the old one.
   * `keepSuperAdminFlag` preserves the synchronously-set super-admin flag when
   * the /me claims haven't propagated yet (impersonation token still carries it).
   */
  refreshFromMe = async (keepSuperAdminFlag: boolean = false): Promise<void> => {
    const tokenAtRequest = window.localStorage.getItem("__accessToken");
    const me: any = await getAuthMe();
    // A token swap (fresh login or client-switch impersonation) can land while
    // this /me was in flight — e.g. the app-mount validateSession() racing the
    // switch-client flow. Applying the stale response would clobber the NEW
    // agency's role/entitlements (and bounce /v2 to the v1 CRM), so drop it.
    if (window.localStorage.getItem("__accessToken") !== tokenAtRequest) return;
    if (me && me.role) {
      this.role = normalizeRole(me.role);
      this.permissions = expandRole(this.role);
      window.localStorage.setItem("role", this.role);
      if (me.AgentId) window.localStorage.setItem("agentId", me.AgentId);
    }
    if (me && me.entitlements && typeof me.entitlements === "object") {
      this.entitlements = me.entitlements;
      window.localStorage.setItem("entitlements", JSON.stringify(me.entitlements));
    } else {
      // No entitlements on this agency — clear the previous agency's leftovers.
      this.entitlements = {};
      window.localStorage.removeItem("entitlements");
    }
    if (me && typeof me.entitlementsConfigured === "boolean") {
      this.entitlementsConfigured = me.entitlementsConfigured;
      window.localStorage.setItem("entitlementsConfigured", String(me.entitlementsConfigured));
    } else {
      this.entitlementsConfigured = false;
      window.localStorage.removeItem("entitlementsConfigured");
    }
    // The agency's plan (realCRM packages matrix) — display-only in the portal;
    // gating itself runs off the effective entitlements map above.
    if (me && me.plan && typeof me.plan === "object" && me.plan.key) {
      window.localStorage.setItem("agencyPlan", JSON.stringify(me.plan));
    } else {
      window.localStorage.removeItem("agencyPlan");
    }
    // Keep the super-admin/impersonation context in sync with the token (the
    // /me response reflects the TOKEN claims, not the acting user's DB row).
    if (me && typeof me.isSuperAdmin === "boolean") {
      this.isSuperAdmin = me.isSuperAdmin;
      if (me.isSuperAdmin) window.localStorage.setItem("__superAdmin", "true");
      else if (!keepSuperAdminFlag) window.localStorage.removeItem("__superAdmin");
    }
    if (me && typeof me.impersonating === "boolean") {
      this.impersonating = me.impersonating;
    }
  };

  /** Confirm the stored token is still valid; on 401 force a logout. */
  validateSession = async () => {
    try {
      await this.refreshFromMe(false);
    } catch (e) {
      // Token missing/expired/invalid — clear the session.
      this.logout();
    }
  };

  logout = () => {
    this.name = "";
    this.isAuthorized = false;
    this.role = "agent_hybrid";
    this.permissions = [];
    this.entitlements = {};
    this.entitlementsConfigured = false;
    this.isSuperAdmin = false;
    this.impersonating = false;
    this.impersonatingAgency = "";
    setAuthorization(undefined);
    // Identity blobs must not survive logout: getSession() prefers agentId and
    // several pages read authUser, so leftovers leak the previous user's scope
    // (including their email-capture address) into the next login.
    window.localStorage.removeItem("authUser");
    window.localStorage.removeItem("agentId");
    window.localStorage.removeItem("entitlements");
    window.localStorage.removeItem("entitlementsConfigured");
    window.localStorage.removeItem("__accessToken");
    window.localStorage.removeItem("__refreshToken");
    window.localStorage.removeItem("__name");
    window.localStorage.removeItem("__username");
    window.localStorage.removeItem("__profilePictureUri");
    window.localStorage.removeItem("role");
    window.localStorage.removeItem("__superAdmin");
    window.localStorage.removeItem("__superAdminToken");
    window.localStorage.removeItem("__superAdminAuthUser");
    window.localStorage.removeItem("__impersonating");
    window.localStorage.removeItem("__impersonatingAgency");
  };

  get isLoggedIn(): boolean {
    return this.isAuthorized;
  }
  getUserInfo = () => {
    return {
      userName: window.localStorage.getItem("__username") ?? "",
      name: window.localStorage.getItem("__name") ?? "",
      profilePictureUri: window.localStorage.getItem("__profilePictureUri") ?? ""
    }
  }
  getUserImage = async () => {
    const res = await getProfileImage();
    const image = URL.createObjectURL(res);
    return image;
  }
}